/**
 * SANDBOXED HTML FRAME — Corpo HTML non fidato (una mail) reso isolato.
 *
 * ## Perche' un iframe
 *
 * L'HTML di una mail e' contenuto non fidato e non entra mai nel DOM dell'app:
 * vive in un iframe con `sandbox`, quindi `<script>` e handler inline non
 * vengono eseguiti. Questo resta il punto fermo.
 *
 * ## Perche' `allow-same-origin` (TS-109)
 *
 * Il token NON riabilita gli script: e' `allow-scripts` a farlo, e qui non c'e'.
 * E' la coppia dei due a essere pericolosa (un frame con entrambi puo' togliersi
 * la sandbox da solo); da solo, `allow-same-origin` lascia il documento inerte e
 * concede al PADRE di leggerne il `contentDocument`.
 *
 * Serve perche' un iframe e' un documento a se': col focus dentro, i suoi
 * `keydown` non raggiungono mai la window dell'app, e le scorciatoie globali —
 * Ctrl/Cmd+K della ricerca, in TopBar — smettevano di funzionare finche' si
 * leggeva una mail. Con l'accesso al documento del frame li si puo' inoltrare.
 *
 * ## Come vengono inoltrati
 *
 * Si ripropone l'evento sulla window dell'app e si guarda se qualcuno l'ha
 * consumato: solo in quel caso si annulla anche l'originale. Cosi' la
 * scorciatoia dell'app vince sul comportamento predefinito del browser, e tutto
 * il resto (Ctrl+C per copiare il testo della mail, le frecce per scorrere)
 * continua a funzionare dentro il frame.
 *
 * ## Altezza sul contenuto (`fitContent`)
 *
 * Di norma il frame ha un'altezza fissa (classe) e scorre dentro. Con `fitContent` prende invece
 * l'altezza del documento, e scorre il contenitore della pagina: serve dove c'è già uno scroll
 * esterno, come nel dialogo della Sorveglianza (POLL-6), e due scroll annidati si intralciano. Anche
 * questo si regge su `allow-same-origin`: senza, il padre non potrebbe misurare il documento. Il
 * `ResizeObserver` segue le immagini che arrivano dopo il `load`.
 */

import { useCallback, useEffect, useRef, type SyntheticEvent } from 'react';

interface SandboxedHtmlFrameProps {
  /** HTML non fidato da rendere. */
  html: string;
  className?: string;
  title: string;
  /** Altezza del frame uguale a quella del documento, senza scroll interno. */
  fitContent?: boolean;
}

export function SandboxedHtmlFrame({ html, className, title, fitContent = false }: SandboxedHtmlFrameProps) {
  const resizeObserver = useRef<ResizeObserver | null>(null);

  useEffect(() => () => resizeObserver.current?.disconnect(), []);

  // Su `load` e non in un effetto: `srcDoc` ricarica il documento a ogni
  // cambio, e il documento vecchio (col suo listener) se ne va con esso.
  const attachKeyForwarder = useCallback((e: SyntheticEvent<HTMLIFrameElement>) => {
    const frame = e.currentTarget;
    const doc = frame.contentDocument;
    if (!doc) return;

    if (fitContent) {
      // Il documento vecchio se ne va con il suo `srcDoc`: l'osservatore riparte sul nuovo.
      resizeObserver.current?.disconnect();
      const fit = () => { frame.style.height = `${doc.documentElement.scrollHeight}px`; };
      fit();
      resizeObserver.current = new ResizeObserver(fit);
      resizeObserver.current.observe(doc.documentElement);
    }

    doc.addEventListener('keydown', (event: KeyboardEvent) => {
      // `new KeyboardEvent('keydown', event)` copia modificatori e tasto: l'init
      // legge le property dall'evento originale.
      const replay = new KeyboardEvent('keydown', event);
      window.dispatchEvent(replay);
      if (replay.defaultPrevented) event.preventDefault();
    });
  }, [fitContent]);

  return (
    <iframe
      className={className}
      title={title}
      scrolling={fitContent ? 'no' : undefined}
      sandbox="allow-same-origin"
      srcDoc={html}
      onLoad={attachKeyForwarder}
    />
  );
}
